import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { menuGroups } from "@/lib/menu";
import { parentMenuGroups, isParentRole } from "@/lib/parent-menu";
import { getMyStaffPermissions } from "@/lib/api/employees.functions";
import { listMyStudentTCs } from "@/lib/api/parent-invites.functions";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Menu Principal" },
      { name: "description", content: "Painel principal do Centro de Treinamento" },
      { property: "og:title", content: "Menu Principal — FutBux" },
      { property: "og:description", content: "Painel principal do Centro de Treinamento" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { role } = Route.useRouteContext() as { role?: string | null };
  const isParent = isParentRole(role);
  const getPerms = useServerFn(getMyStaffPermissions);
  const listTcs = useServerFn(listMyStudentTCs);
  const [isOwner, setIsOwner] = useState(true);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [parentTcCount, setParentTcCount] = useState(0);

  useEffect(() => {
    (async () => {
      if (isParent) {
        setIsOwner(false);
        setAllowed(new Set());
        setParentTcCount(1);
        setLoaded(true);
        return;
      }
      const [permsRes, tcsRes] = await Promise.allSettled([getPerms(), listTcs()]);
      if (permsRes.status === "fulfilled") {
        setIsOwner(!!permsRes.value.isOwner);
        setAllowed(new Set(permsRes.value.permissions ?? []));
      } else {
        setIsOwner(false);
        setAllowed(new Set());
      }
      if (tcsRes.status === "fulfilled") {
        setParentTcCount((tcsRes.value.tcs ?? []).length);
      }
      setLoaded(true);
    })();
  }, [getPerms, listTcs, isParent]);

  const canSee = (slug: string) => isOwner || allowed.has(slug);

  if (!loaded) return null;


  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{isParent ? "Portal do Usuário" : "Centro de Treinamento"}</h1>
        <p className="text-muted-foreground text-sm">
          Selecione uma opção do menu principal.
        </p>
      </div>

      {parentTcCount > 0 && parentMenuGroups.map((group) => (
        <section key={group.label} className="space-y-3">
          <h2 className="text-base font-semibold text-foreground/80">{group.label}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.title} to={item.to} className="group">
                  <Card className="flex items-center gap-3 p-4 transition-all hover:border-primary hover:shadow-md">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-medium">{item.title}</span>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      {!isParent && menuGroups.map((group) => {
        const items = group.items.filter((i) => canSee(i.slug));
        if (items.length === 0) return null;
        return (
          <section key={group.label} className="space-y-3">
            <h2 className="text-base font-semibold text-foreground/80">{group.label}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.slug}
                    to="/m/$slug"
                    params={{ slug: item.slug }}
                    className="group"
                  >
                    <Card className="flex items-center gap-3 p-4 transition-all hover:border-primary hover:shadow-md">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
